const productKey = (product) => JSON.stringify([product.title, product.category]);

export class ProductImportError extends Error {
  constructor(message, details) {
    super(message);
    Object.assign(this, details);
  }
}

export default async function importPosProducts({
  rows,
  products,
  replacementIds,
  validate,
  buildData,
  createId,
  commit,
}) {
  const issues = [];
  for (let index = 0; index < rows.length; index += 1) {
    try {
      await validate(rows[index]);
    } catch (error) {
      issues.push({
        line: rows[index].__importRow || index + 2,
        title: rows[index].title || "",
        message: (error.errors || [error.message]).join("; "),
      });
    }
  }
  if (issues.length) {
    throw new ProductImportError("Invalid product rows", { phase: "validation", issues });
  }

  const byKey = new Map();
  products.forEach((product) => {
    const key = productKey(product);
    byKey.set(key, [...(byKey.get(key) || []), product]);
  });
  // Match the existing title/category import contract. When a key appears
  // several times in the file, the last row supplies its final values.
  const imported = new Map(rows.map((row) => [productKey(row), row]));
  const operations = [];
  const replacementSet = new Set(replacementIds);
  products.forEach((product) => {
    if (replacementSet.has(product.id) && !imported.has(productKey(product))) {
      operations.push({ type: "delete", id: product.id });
    }
  });
  imported.forEach((row, key) => {
    const matches = byKey.get(key) || [];
    if (matches.length) {
      matches.forEach((existing) => {
        const data = buildData(row, existing);
        if (
          Object.keys(data).some(
            (field) => JSON.stringify(data[field]) !== JSON.stringify(existing[field]),
          )
        ) {
          operations.push({ type: "update", id: existing.id, data });
        }
      });
    } else {
      const id = createId();
      operations.push({ type: "set", id, data: { ...buildData(row), id } });
    }
  });

  const completed = { update: 0, set: 0, delete: 0 };
  for (let offset = 0; offset < operations.length; offset += 400) {
    const batch = operations.slice(offset, offset + 400);
    try {
      await commit(batch);
      batch.forEach((operation) => {
        completed[operation.type] += 1;
      });
    } catch (error) {
      throw new ProductImportError(error.message || "Product writes failed", {
        phase: "write",
        completed: { ...completed },
        code: error.code,
      });
    }
  }
  return completed;
}
